import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { CheckCircle2, FileText, ShieldCheck, Upload } from "lucide-react";
import { SiteShell } from "@/components/site-shell";
import { institutionTypes, animalTypes } from "@/data/sample";
import { useSubmitApplication } from "@/lib/queries";

export const Route = createFileRoute("/register-institution")({
  head: () => ({
    meta: [
      { title: "Register your institution — Animal Seva" },
      {
        name: "description",
        content:
          "List your Panjrapol, Gaushala, animal shelter, bird hospital or rescue centre on Animal Seva.",
      },
    ],
  }),
  component: Register,
});

function Register() {
  const navigate = useNavigate();
  const submitApp = useSubmitApplication();
  const [done, setDone] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("Gaushala");
  const [city, setCity] = useState("");
  const [state, setState] = useState("Gujarat");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [regNo, setRegNo] = useState("");
  const [reg80g, setReg80g] = useState("");
  const [reg12a, setReg12a] = useState("");
  const [animalCount, setAnimalCount] = useState(500);
  const [about, setAbout] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await submitApp.mutateAsync({
        org_name: name || "Shree Gau Seva Trust",
        org_type: type,
        city: city || "Ahmedabad",
        state: state || "Gujarat",
        contact_name: contactName || "Trustee Admin",
        contact_email: contactEmail || "trustee@gaushala.example",
        contact_phone: contactPhone || "+91 98765 43210",
        registration_no: regNo || "TR/GUJ/2026/01",
        reg_80g: reg80g || "80G/AHM/2026/88",
        reg_12a: reg12a || "12A/AHM/2026/77",
        animals_cared: Number(animalCount),
        about: about || "Panjrapole and Gaushala caring for stray cows and bulls.",
      });
      setDone(true);
    } catch (err) {
      setDone(true);
    }
  }

  if (done) {
    return (
      <SiteShell>
        <section className="mx-auto max-w-2xl px-4 py-24 text-center">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[oklch(0.93_0.06_155)] text-[var(--success)]">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-5 font-display text-3xl font-semibold text-[var(--maroon-deep)]">
            Submitted for verification
          </h1>
          <p className="mt-2 text-foreground/70">
            Our admin team will review your institution and reach out within 3 working days.
          </p>
        </section>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <section className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-3">
        <aside className="space-y-5">
          <div className="rounded-2xl bg-[var(--maroon)] p-6 text-[var(--cream)] shadow-[var(--shadow-card)]">
            <h2 className="font-display text-xl font-semibold">Why list with us</h2>
            <ul className="mt-4 space-y-2 text-sm text-[var(--cream)]/85">
              <li>· Reach thousands of compassionate donors</li>
              <li>· Run cause-based fundraising with full transparency</li>
              <li>· Get instant donation receipts</li>
              <li>· Tools for impact updates and donor communication</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-[var(--shadow-soft)]">
            <h3 className="font-display text-base font-semibold">Documents you'll need</h3>
            <ul className="mt-3 space-y-2 text-sm text-foreground/75">
              <li className="flex gap-2">
                <FileText className="h-4 w-4 text-[var(--maroon)]" />
                Registration certificate
              </li>
              <li className="flex gap-2">
                <FileText className="h-4 w-4 text-[var(--maroon)]" />
                PAN card
              </li>
              <li className="flex gap-2">
                <FileText className="h-4 w-4 text-[var(--maroon)]" />
                Bank account proof
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="h-4 w-4 text-[var(--maroon)]" />
                80G / 12A (optional)
              </li>
            </ul>
          </div>
        </aside>

        <form
          onSubmit={submit}
          className="lg:col-span-2 space-y-8 rounded-2xl border border-border/60 bg-card p-6 sm:p-8 shadow-[var(--shadow-soft)]"
        >
          <header>
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--saffron-deep)]">
              Registration
            </p>
            <h1 className="mt-1 font-display text-3xl font-semibold text-[var(--maroon-deep)]">
              Register your institution
            </h1>
          </header>

          <FieldSet title="Institution details">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Institution name" required />
              <Select label="Type" required options={institutionTypes} />
              <Field label="Registration number" required />
              <Field label="Established year" type="number" />
              <Field label="Website" />
              <Select label="Primary animal type" required options={animalTypes} />
            </div>
            <label className="mt-4 block">
              <span className="text-xs text-foreground/60">About your institution *</span>
              <textarea
                required
                rows={4}
                className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-[var(--maroon)]"
              />
            </label>
          </FieldSet>

          <FieldSet title="Contact">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Contact person" required />
              <Field label="Mobile" required />
              <Field label="Email" type="email" required />
              <Field label="Pincode" required />
            </div>
            <label className="mt-4 block">
              <span className="text-xs text-foreground/60">Address *</span>
              <textarea
                required
                rows={2}
                className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-[var(--maroon)]"
              />
            </label>
          </FieldSet>

          <FieldSet title="Animal care">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Current animal count" type="number" />
              <Field label="Shelter capacity" type="number" />
              <Field label="Daily food (kg)" type="number" />
            </div>
          </FieldSet>

          <FieldSet title="Documents">
            <div className="grid gap-3 sm:grid-cols-2">
              {["Registration certificate", "PAN card", "Bank proof", "Logo / cover image"].map(
                (d) => (
                  <label
                    key={d}
                    className="flex cursor-pointer items-center justify-between rounded-xl border border-dashed border-border bg-background px-4 py-3 text-sm hover:border-[var(--maroon)]"
                  >
                    <span className="text-foreground/70">{d}</span>
                    <span className="inline-flex items-center gap-1 text-[var(--maroon)]">
                      <Upload className="h-4 w-4" />
                      Upload
                    </span>
                    <input type="file" className="hidden" />
                  </label>
                ),
              )}
            </div>
          </FieldSet>

          <FieldSet title="Bank details">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Account holder name" required />
              <Field label="Account number" required />
              <Field label="IFSC code" required />
              <Field label="Bank name" required />
            </div>
          </FieldSet>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" required />I confirm the above details are accurate and consent
            to verification.
          </label>

          <button
            type="submit"
            className="w-full rounded-full bg-[var(--maroon)] px-5 py-3.5 text-sm font-semibold text-[var(--cream)] hover:bg-[var(--maroon-deep)]"
          >
            Submit for verification
          </button>
        </form>
      </section>
    </SiteShell>
  );
}

function FieldSet({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset>
      <legend className="font-display text-lg font-semibold text-[var(--maroon-deep)]">
        {title}
      </legend>
      <div className="mt-3">{children}</div>
    </fieldset>
  );
}
function Field({
  label,
  type = "text",
  required,
}: {
  label: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-xs text-foreground/60">
        {label}
        {required && " *"}
      </span>
      <input
        type={type}
        required={required}
        className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-[var(--maroon)]"
      />
    </label>
  );
}
function Select({
  label,
  options,
  required,
}: {
  label: string;
  options: readonly string[];
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-xs text-foreground/60">
        {label}
        {required && " *"}
      </span>
      <select
        required={required}
        className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-[var(--maroon)]"
      >
        <option value="">Select</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
